import React, { useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import IdleTimer from 'react-idle-timer';
import { useQuery } from '@apollo/client';

import PrimaryButton from '@common/button';
import {
  ActionType,
  Flow,
  FormState,
  FormValidation,
  useBookingContext,
} from '@common/context/BookingContext';
import { useCategoryContext } from '@common/context/CategoryContext';
import { AddressDetails } from '@common/fields/AddressField/googleMaps';
import useFeatureFlag, {
  useFeatureFlagSetting,
} from '@common/utils/hooks/useFeatureFlag';
import useQueryParams from '@common/utils/hooks/useQueryParams';
import { logout } from '@common/utils/token';
import {
  AnswerInput,
  ContactsQuery_contacts_contacts as Contact,
  LeadSegmentQuery,
  MeetingTypeLabel,
  OfficesQuery_offices as Office,
  Timetable_timetable_groups_rows_columns as Column,
} from '@typings/api';
import { SELF_SERVICE_PATH } from '@utils/constants';

import { LEAD_SEGMENT } from '../customerForm/queries';

import { Contacts } from './Contacts/Contacts';
import { AddressProvider } from './Offices/AddressContext';
import ConfirmModal from './ConfirmModal';
import { Listings } from './Listings';
import MeetingTypes from './MeetingTypes';
import Offices from './Offices';
import Subject from './Subject';
import TimeTables from './TimeTable';

export enum MainFormFields {
  ADDRESS = 'address',
  ADDRESS_KEY = 'addressKey',
  LISTING = 'listingId',
  SUBJECT = 'subjectId',
  MEETING_TYPE = 'meetingType',
  OFFICES = 'offices',
  CONTACT = 'contact',
  TIME_SLOT = 'timeSlot',
  SELECTED_OFFICE = 'selectedOffice',
}

export interface FormValues {
  address?: AddressDetails;
  addressKey?: string;
  answers?: AnswerInput[];
  subjectId?: string;
  meetingType?: MeetingTypeLabel;
  listingId?: string;
  leadSegmentId?: string;
  offices: Office[];
  selectedOffice?: Office;
  contact?: Contact;
  timeSlot: Column;
}

export default function BookingFlow() {
  const { id: contextId } = useQueryParams();
  const { t } = useTranslation();
  const [
    {
      answers,
      callbackEnabled,
      customerFormState,
      customerFormValidation,
      customerValid,
    },
    dispatch,
  ] = useBookingContext();

  const isContactsEnabled = useFeatureFlag('steps.contacts.enabled');
  const isListingsEnabled = useFeatureFlag('steps.listings.enabled');
  const bookingTimeoutEnabled = useFeatureFlag('timeout.booking.enabled', true);
  const bookingTimoutTime = useFeatureFlagSetting(
    'timeout.booking.setting.timeout',
  );

  const [submittedValues, setSubmittedValues] = useState<
    FormValues | undefined
  >(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { category } = useCategoryContext();

  const { data: leadSegmentData } = useQuery<LeadSegmentQuery>(LEAD_SEGMENT, {
    variables: {
      contextId,
    },
    skip: !contextId,
  });

  function handleKeyPress(event: React.KeyboardEvent<HTMLFormElement>) {
    // Disable submitting form on enter to prevent incorrect flows due to nested form setup
    if (event.which === 13) {
      event.preventDefault();
    }
  }

  function handleSubmit(values: FormValues) {
    setSubmittedValues({
      ...values,
      answers: answers ?? [],
    });

    dispatch({
      type: ActionType.SET_CUSTOMER_FORM_VALIDATION,
      payload: FormValidation.FULL,
    });
  }

  async function handleIdle() {
    await logout();
    window.location.replace(SELF_SERVICE_PATH);
  }

  function setReferralFlow() {
    return dispatch({
      type: ActionType.SET_FLOW,
      payload: Flow.REFERRAL,
    });
  }

  useEffect(() => {
    if (
      customerFormValidation === FormValidation.FULL &&
      customerValid &&
      submittedValues
    ) {
      setShowConfirmModal(true);
    } else if (
      submittedValues &&
      customerFormValidation === FormValidation.FULL
    ) {
      window.scroll(0, 0);
    }
  }, [customerFormValidation, customerValid, submittedValues]);

  return (
    <>
      <Form
        initialValues={{ leadSegmentId: leadSegmentData?.leadSegment?.id }}
        onSubmit={handleSubmit}
      >
        {({
          handleSubmit,
          values: { listingId, subjectId, meetingType, offices = [], contact },
        }) => (
          <form onKeyPress={handleKeyPress} onSubmit={handleSubmit}>
            <div className="space-y-4">
              <AddressProvider>
                <Listings />
                {(!isListingsEnabled || listingId !== undefined) && <Subject />}
                {subjectId !== undefined && <MeetingTypes />}
                {meetingType !== undefined && <Offices />}
                {isContactsEnabled && offices.length > 0 && <Contacts />}
                {offices.length > 0 &&
                  (!isContactsEnabled || contact !== undefined) && (
                    <TimeTables />
                  )}
              </AddressProvider>
            </div>
          </form>
        )}
      </Form>
      {submittedValues && (
        <ConfirmModal
          formValues={submittedValues}
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
      {category?.selfService === true && bookingTimeoutEnabled && (
        <IdleTimer
          onIdle={handleIdle}
          timeout={parseInt(bookingTimoutTime ?? DEFAULT_TIMEOUT_TIME, 10)}
        />
      )}
      {customerFormState === FormState.COLLAPSED && callbackEnabled && (
        <div className="fab-container">
          <PrimaryButton onClick={setReferralFlow}>
            {t('main_form.buttons.referral_flow')}
          </PrimaryButton>
        </div>
      )}
    </>
  );
}

const DEFAULT_TIMEOUT_TIME = process.env.IDLE_TIMER_BOOKING ?? '60000';
